import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { className as _$className } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=left-panel><div class="address-search panel-section"><label>SEARCH ANY LOCATION</label><form class=search-form><input type=search class=search-input placeholder="address, city, landmark..."autocomplete=off autocapitalize=off><button type=submit class=search-btn></button></form></div><div class="poi-navigation panel-section"><button class=nav-btn id=prev-poi>PREV</button><span class=poi-display id=poi-display></span><button class=nav-btn id=next-poi>NEXT</button></div><div class="toggles panel-section"></div><div class="action-buttons panel-section"><button class=action-btn disabled>AUTO CAL</button><button class=action-btn disabled>ALIGN - DRAPE</button></div><div class=system-log><div class=log-header>SYSTEM LOG</div><div class=log-content id=system-log-content>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=toggle-row><span></span><button>`),
  _tmpl$3 = /*#__PURE__*/_$template(`<div class=ground-options-row>`),
  _tmpl$4 = /*#__PURE__*/_$template(`<div class=ground-options-row><div class=sub-toggle-row><button>TRAFFIC</button><button></button></div><div class=sub-toggle-row><button>CCTV</button><button>SEISMIC</button></div><div class=sub-toggle-row><button>THREAT</button><button>SUN`),
  _tmpl$5 = /*#__PURE__*/_$template(`<div class=sub-toggle-row><button>`),
  _tmpl$6 = /*#__PURE__*/_$template(`<div>`);
/**
 * WorldView - Left Panel (SolidJS)
 * City selector, POI navigation, and layer toggles
 */

import { createSignal, createMemo, For, Show, onMount } from "solid-js";
import { layers, toggleLayer } from "../stores/layers";
import { groundState, toggleSubLayer, setTrafficStyle } from "../layers/ground/store";
import { satelliteState, toggleCategory } from "../layers/satellites/store";
import { ui, setCurrentCityIndex, setCurrentPOIIndex, setMyLocationActive } from "../stores/ui";
import { useCesium } from "../cesium/useCesium";
import { getUserLocation } from "../cesium/getUserLocation";
import { CCTVCameraListPanel } from "./panels/CCTVCameraListPanel";
import { cities, flyToPOI } from "./navigation";
import { PROXY_ENDPOINTS } from "../config";

// Layer display configuration

// Static layer configuration for display
const LAYER_CONFIGS = [{
  id: "satellites",
  name: "SATELLITES"
}, {
  id: "ships",
  name: "SHIPS"
}, {
  id: "planes",
  name: "PLANES"
}, {
  id: "ground",
  name: "GROUND"
}];
const SATELLITE_CATEGORIES = [{
  id: "stations",
  name: "STATIONS"
}, {
  id: "military",
  name: "MILITARY"
}, {
  id: "gnss",
  name: "GNSS"
}, {
  id: "research",
  name: "RESEARCH"
}, {
  id: "starlink",
  name: "STARLINK"
}];
let logIdCounter = 0;

/**
 * LeftPanel component
 */
export function LeftPanel() {
  const {
    viewer
  } = useCesium();

  // System log entries
  const [logEntries, setLogEntries] = createSignal([{
    id: logIdCounter++,
    message: "[INIT] System ready",
    type: "success"
  }, {
    id: logIdCounter++,
    message: "[INFO] Google 3D Tiles active",
    type: "info"
  }]);

  // Address search state
  const [searchQuery, setSearchQuery] = createSignal("");
  const [searchBusy, setSearchBusy] = createSignal(false);
  async function handleAddressSearch(e) {
    e.preventDefault();
    const q = searchQuery().trim();
    if (!q || searchBusy()) return;
    setSearchBusy(true);
    addLogEntry(`[SEARCH] "${q}"`);
    try {
      const r = await fetch(PROXY_ENDPOINTS.geocode(q));
      const j = await r.json();
      if (!j.ok || !j.results?.length) {
        addLogEntry(`[SEARCH] no results for "${q}"`, "error");
        return;
      }
      const top = j.results[0];
      const {
        lat,
        lng
      } = top.geometry.location;
      const altitude = top.types?.includes("airport") ? 1500 : 600;
      flyToPOI(viewer(), {
        name: top.formatted_address,
        lat,
        lng,
        altitude,
        pitch: -45
      });
      // 2026-08-31 — mark that the OPERATOR moved the camera deliberately. The
      // native GPS re-centre (window.atlasFocusEntity) checks this and stands down,
      // so a search is never yanked back to the operator's position mid-flight.
      window.__atlasOperatorMovedCamera = true;
      setCurrentCityIndex(-1);
      setMyLocationActive(false);
      addLogEntry(`[NAV] ${top.formatted_address}`, "success");
      setSearchQuery("");
    } catch (err) {
      addLogEntry(`[SEARCH] failed: ${String(err)}`, "error");
    } finally {
      setSearchBusy(false);
    }
  }

  // Derived state from shared store
  const currentCity = createMemo(() => cities[ui.currentCityIndex]);
  const currentPOI = createMemo(() => currentCity()?.pois[ui.currentPOIIndex]);
  const poiCount = createMemo(() => currentCity()?.pois.length ?? 0);

  // Navigation helpers
  const canGoPrev = createMemo(() => ui.currentPOIIndex > 0);
  const canGoNext = createMemo(() => ui.currentPOIIndex < poiCount() - 1);
  function addLogEntry(message, type = "info") {
    setLogEntries(prev => [{
      id: logIdCounter++,
      message,
      type
    }, ...prev].slice(0, 20));
  }
  function handleCityChange(e) {
    const index = parseInt(e.target.value, 10);
    setCurrentCityIndex(index);
    setCurrentPOIIndex(0);
    addLogEntry(`[NAV] Flying to ${cities[index]?.name}`);
    const firstPOI = cities[index]?.pois[0];
    if (firstPOI) flyToPOI(viewer(), firstPOI);
  }
  function navigatePOI(delta) {
    const newIndex = ui.currentPOIIndex + delta;
    if (newIndex < 0 || newIndex >= poiCount()) return;
    setCurrentPOIIndex(newIndex);
    const poi = currentCity()?.pois[newIndex];
    if (poi) {
      addLogEntry(`[NAV] POI: ${poi.name}`);
      flyToPOI(viewer(), poi);
    }
  }
  function handleToggleLayer(id) {
    const wasEnabled = layers[id];
    toggleLayer(id);
    addLogEntry(`[${id.toUpperCase()}] Layer ${wasEnabled ? "disabled" : "enabled"}`);
  }
  function handleToggleCategory(category) {
    const wasVisible = !satelliteState.hiddenCategories.has(category);
    toggleCategory(category);
    addLogEntry(`[SAT] ${category.toUpperCase()} ${wasVisible ? "OFF" : "ON"}`);
  }
  function handleToggleSubLayer(subLayer) {
    const stateKey = `${subLayer}Enabled`;
    const wasEnabled = groundState[stateKey];
    toggleSubLayer(subLayer);
    addLogEntry(`[GROUND] ${subLayer.toUpperCase()} ${wasEnabled ? "OFF" : "ON"}`);
  }
  function handleCycleTrafficStyle() {
    const nextStyle = groundState.trafficStyle === "heatmap" ? "terminal" : "heatmap";
    setTrafficStyle(nextStyle);
    addLogEntry(`[GROUND] Style: ${nextStyle.toUpperCase()}`);
  }
  onMount(() => {
    // Start geolocation request immediately (runs in parallel with viewer-ready delay)
    const locationPromise = getUserLocation();

    // Fly to user's location or fall back to initial POI
    setTimeout(async () => {
      const userLocation = await locationPromise;
      if (userLocation) {
        setCurrentCityIndex(-1);
        setMyLocationActive(true);
        flyToPOI(viewer(), {
          name: "User Location",
          lat: userLocation.lat,
          lng: userLocation.lng,
          altitude: 500,
          pitch: -45
        });
      } else {
        const poi = currentPOI();
        if (poi) flyToPOI(viewer(), poi);
      }
    }, 2000);
  });
  return (() => {
    var _el$ = _tmpl$(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.firstChild,
      _el$4 = _el$3.nextSibling,
      _el$5 = _el$4.firstChild,
      _el$6 = _el$5.nextSibling,
      _el$7 = _el$2.nextSibling,
      _el$8 = _el$7.firstChild,
      _el$9 = _el$8.nextSibling,
      _el$0 = _el$9.nextSibling,
      _el$1 = _el$7.nextSibling,
      _el$10 = _el$1.nextSibling,
      _el$11 = _el$10.nextSibling,
      _el$12 = _el$11.firstChild,
      _el$13 = _el$12.nextSibling;
    _el$4.addEventListener("submit", handleAddressSearch);
    _el$5.$$input = e => setSearchQuery(e.currentTarget.value);
    _$setAttribute(_el$5, "spellcheck", false);
    _$insert(_el$6, () => searchBusy() ? "..." : "GO");
    _el$8.$$click = () => navigatePOI(-1);
    _$insert(_el$9, () => currentPOI()?.name ?? "No POI");
    _el$0.$$click = () => navigatePOI(1);
    _$insert(_el$1, _$createComponent(For, {
      each: LAYER_CONFIGS,
      children: config => [(() => {
        var _el$14 = _tmpl$2(),
          _el$15 = _el$14.firstChild,
          _el$16 = _el$15.nextSibling;
        _$insert(_el$15, () => config.name);
        _el$16.$$click = () => handleToggleLayer(config.id);
        _$insert(_el$16, () => layers[config.id] ? "ON" : "OFF");
        _$effect(() => _$className(_el$16, `toggle-btn ${layers[config.id] ? "on" : ""}`));
        return _el$14;
      })(), _$createComponent(Show, {
        get when() {
          return _$memo(() => config.id === "satellites")() && layers.satellites;
        },
        get children() {
          var _el$17 = _tmpl$3();
          _$insert(_el$17, _$createComponent(For, {
            each: SATELLITE_CATEGORIES,
            children: cat => (() => {
              var _el$28 = _tmpl$5(),
                _el$29 = _el$28.firstChild;
              _el$29.$$click = () => handleToggleCategory(cat.id);
              _$insert(_el$29, () => cat.name);
              _$effect(() => _$className(_el$29, `toggle-btn sub-toggle ${!satelliteState.hiddenCategories.has(cat.id) ? "on" : ""}`));
              return _el$28;
            })()
          }));
          return _el$17;
        }
      }), _$createComponent(Show, {
        get when() {
          return _$memo(() => config.id === "ground")() && layers.ground;
        },
        get children() {
          var _el$18 = _tmpl$4(),
            _el$19 = _el$18.firstChild,
            _el$20 = _el$19.firstChild,
            _el$21 = _el$20.nextSibling,
            _el$22 = _el$19.nextSibling,
            _el$23 = _el$22.firstChild,
            _el$24 = _el$23.nextSibling,
            _el$25 = _el$22.nextSibling,
            _el$26 = _el$25.firstChild,
            _el$27 = _el$26.nextSibling;
          _el$20.$$click = () => handleToggleSubLayer("traffic");
          _el$21.$$click = handleCycleTrafficStyle;
          _$insert(_el$21, () => groundState.trafficStyle.toUpperCase());
          _el$23.$$click = () => handleToggleSubLayer("cctv");
          _el$24.$$click = () => handleToggleSubLayer("seismic");
          _el$26.$$click = () => handleToggleSubLayer("threat");
          _el$27.$$click = () => handleToggleSubLayer("sun");
          _$effect(_p$ => {
            var _v$5 = `toggle-btn sub-toggle ${groundState.trafficEnabled ? "on" : ""}`,
              _v$6 = `toggle-btn style-toggle ${groundState.trafficEnabled ? "" : "disabled"}`,
              _v$7 = !groundState.trafficEnabled,
              _v$8 = `toggle-btn sub-toggle ${groundState.cctvEnabled ? "on" : ""}`,
              _v$9 = `toggle-btn sub-toggle ${groundState.seismicEnabled ? "on" : ""}`,
              _v$0 = `toggle-btn sub-toggle ${groundState.threatHeatmapEnabled ? "on" : ""}`,
              _v$1 = `toggle-btn sub-toggle ${groundState.sunStudyEnabled ? "on" : ""}`;
            _v$5 !== _p$.e && _$className(_el$20, _p$.e = _v$5);
            _v$6 !== _p$.t && _$className(_el$21, _p$.t = _v$6);
            _v$7 !== _p$.a && (_el$21.disabled = _p$.a = _v$7);
            _v$8 !== _p$.o && _$className(_el$23, _p$.o = _v$8);
            _v$9 !== _p$.i && _$className(_el$24, _p$.i = _v$9);
            _v$0 !== _p$.n && _$className(_el$26, _p$.n = _v$0);
            _v$1 !== _p$.s && _$className(_el$27, _p$.s = _v$1);
            return _p$;
          }, {
            e: undefined,
            t: undefined,
            a: undefined,
            o: undefined,
            i: undefined,
            n: undefined,
            s: undefined
          });
          return _el$18;
        }
      })]
    }));
    _$insert(_el$, _$createComponent(CCTVCameraListPanel, {}), _el$11);
    _$insert(_el$13, _$createComponent(For, {
      get each() {
        return logEntries();
      },
      children: entry => (() => {
        var _el$30 = _tmpl$6();
        _$insert(_el$30, () => entry.message);
        _$effect(() => _$className(_el$30, `log-entry ${entry.type}`));
        return _el$30;
      })()
    }));
    _$effect(_p$ => {
      var _v$ = searchBusy(),
        _v$2 = searchBusy() || !searchQuery().trim(),
        _v$3 = !canGoPrev(),
        _v$4 = !canGoNext();
      _v$ !== _p$.e && (_el$5.disabled = _p$.e = _v$);
      _v$2 !== _p$.t && (_el$6.disabled = _p$.t = _v$2);
      _v$3 !== _p$.a && (_el$8.disabled = _p$.a = _v$3);
      _v$4 !== _p$.o && (_el$0.disabled = _p$.o = _v$4);
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined
    });
    _$effect(() => _el$5.value = searchQuery());
    return _el$;
  })();
}
export default LeftPanel;
_$delegateEvents(["input", "click"]);